import * as React from "react";
import { useEffect, useState } from "react";
import { useAuth } from "@airborne/react-admin";
import { Datagrid, TextField, useDataProvider, useRefresh } from "react-admin";
import FilterToolbar from "../../../Components/FiltersToolbar/FilterToolbar";
import { DefaultPerPageSize } from "../../../Constants/Constant";
import { FilterInputType, FilterTypes } from "../../../Enums/FilterTypes";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { CustomDateField } from "../../../Components/CustomDateField";
import { DetailButton } from "../../../Components/DetailButton";
import { Box, ButtonGroup, IconButton, makeStyles, Tooltip, Zoom } from "@material-ui/core";
import ListActions from "./ListActions";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";
import CopyableTableWrapper from "../../../../src/Components/CopyableTableWrapper";
import { Create } from "@material-ui/icons";
import RequestRemark from "../../../Pages/RequestOrder/Components/PbhRequestOrderList/Notes/RequestRemark";
import { getEnumDescription } from "../../../Helper/EnumHelper";
import { GetPbhRemoveReasonType } from "../../../Enums/PbhRemoveReasonType";
import { GetPbhRemoveReasonSubType } from "../../../Enums/RemoveReasonSubType";
import {
  LocalRepairChoices,
  IsWaitingForCustomeRecordChoices,
} from "../../RequestOrder/Constant/Source";

const useStyles = makeStyles({
  datagrid: {
    "& .MuiTableCell-root": {
      padding: "0px !important", // Hücreler arasındaki padding'i sıfırlar
      marginLeft: "0px !important",
      marginRight: "0px !important",
    },
    "& .MuiTableCell-head": {
      padding: "0px 2px 0px 2px !important", // Başlık hücreleri için padding'i sıfırlar
    },
    "& .MuiTableCell-body": {
      padding: "0px", // Gövde hücreleri için padding'i sıfırlar
    },
    "& .MuiTableCell-sizeSmall": {
      padding: "0px 2px 0px 2px !important", // Gövde hücreleri için padding'i sıfırlar
    },
  },
});

const PbhSearch = props => {
  const { user } = useAuth();
  const classes = useStyles();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const [customers, setCustomers] = useState<any>();
  const [request, setRequest] = useState<any>({});
  const [isMatchCreate, setIsMatchCreate] = useState(false);

  useEffect(() => {
    getCustomerData();
  }, []);

  const getCustomerData = async () => {
    await dataProvider
      .getList("cdmheaders", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "ownerName", order: "ASC" },
        filter: {},
      })
      .then(response => {
        const customerCodes = [...new Set(response.data?.map(x => x.ownerName))];
        const customerData = customerCodes
          ?.map(ownerName => ({ id: ownerName, customerCode: ownerName }))
          .sort((a, b) => a.id?.localeCompare(b.id));
        setCustomers(customerData);
      });
  };

  const remarkOnClick = props => e => {
    e.stopPropagation();
    setRequest(props);
    setIsMatchCreate(true);
  };

  const exporter = records => {
    // will call dataProvider.getMany('cdmaircraft', { ids: records.map(record => record.aircraftId) }), ignoring duplicate and empty aircraftId
    const data = records.map(record => {
      var result = {
        //...record,
        "Customer Code": record.customerName,
        "Ref No": record.refNo,
        Category: record.category,
        Pn: record.pn,
        PnDescription: record.pnDescription,
        "Airport Code": record.airportCode,
        "Ac Reg": record.acRegistration,
        "Customer Po": record.customerPo,
        "Request Date": record.requestDate,
        "Requested Delivery Date": record.requestedDeliveryDate,
        "Serviceable Forward Date": record.svcForwardDate,
        "Serviceable Estimated Delivery Date": record.svcEstimatedDeliveryDate,
        "Core Unit Forward Date": record.unsvcForwardDate,
        "Core Unit Estimated Delivery Date": record.unsvcEstimatedDeliveryDate,
        "Request Status": record.requestStatusText,
        "Svc Awb": record.svcAwb,
        "Svc Pn": record.svcPn,
        "Svc Sn": record.svcSn,
        "Core Awb": record.unsvcAwb,
        "Core Pn": record.unsvcPn,
        "Core Sn": record.unsvcSn,
        Serviceable: record.serviceable,
        "Core Unit": record.unserviceable,
        "Serviceable Shipped On Time": record.serviceableShippedOnTime,
        "Excusable Delay Status": record.excusableDelayStatus,
        "Serviceable Shipment Type": record.shipmentType,
        "Xo Number": record.xoNo,
        "Remove Reason Type": getEnumDescription(
          record.pbhRequestUnserviceable?.removeReasonType,
          GetPbhRemoveReasonType
        ),
        "Remove Reason Sub Type": getEnumDescription(
          record.pbhRequestUnserviceable?.removeReasonSubType,
          GetPbhRemoveReasonSubType(record.pbhRequestUnserviceable?.removeReasonType) || []
        ),
      };

      if (!user?.isCustomer) {
        result = { ...result, ...{ "PBH Note": record.note } };
      }

      return result;
    });

    const columns = Object.keys(data[0]).map(record => ({ header: record }));

    ExcelExport(columns, data, "Search List");
  };

  const Actions = props => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailButton path="pbhrequestorders" {...props} />
          {user?.isCustomer === false && <RemarkButton {...props} />}
        </ButtonGroup>
      </Box>
    );
  };

  const RemarkButton = props => {
    return (
      <IconButton style={{ width: 40 }} onClick={remarkOnClick(props.record)}>
        <Tooltip TransitionComponent={Zoom} title="Add Note">
          <Create />
        </Tooltip>
      </IconButton>
    );
  };

  return (
    <>
      <PoolList
        {...props}
        title={"Search"}
        hasCreate={false}
        exporter={exporter}
        resource="pbhrequestorders/search"
        basePath="search"
        bulkActionButtons={false}
        perPage={DefaultPerPageSize}
        actions={<ListActions {...props} />}
        filters={
          <FilterToolbar
            dataSource={[
              ...(!user?.isCustomer
                ? [
                    {
                      source: "ownerCode",
                      inputType: FilterInputType.AutoCompleteArray,
                      label: "Customer",
                      disabled: user?.isCustomer,
                      filterTypeChangeDisable: true,
                      filterType: FilterTypes.In,
                      choices: customers,
                      // defValue: customers?.[0]?.customerCode,
                      optionValue: "id",
                      optionText: "id",
                      resettable: false,
                    },
                  ]
                : []),
              {
                source: "pn",
                inputType: FilterInputType.Text,
                label: "Pn",
              },
              {
                source: "refNo",
                inputType: FilterInputType.Text,
                label: "Ref No",
              },
              {
                source: "category",
                inputType: FilterInputType.Text,
                label: "Category",
              },
              {
                source: "requestStatusText",
                inputType: FilterInputType.Text,
                label: "Request Status",
              },
              {
                source: "svcPn",
                inputType: FilterInputType.Text,
                label: "Svc Pn",
              },
              {
                source: "svcSn",
                inputType: FilterInputType.Text,
                label: "Svc Sn",
              },
              {
                source: "svcAwb",
                inputType: FilterInputType.Text,
                label: "Svc Awb",
              },
              {
                source: "unsvcPn",
                inputType: FilterInputType.Text,
                label: "Core PN",
              },
              {
                source: "unsvcSn",
                inputType: FilterInputType.Text,
                label: "Core SN",
              },
              {
                source: "unsvcAwb",
                inputType: FilterInputType.Text,
                label: "Core Awb",
              },
              {
                source: "acRegistration",
                inputType: FilterInputType.Text,
                label: "Ac Reg",
              },
              {
                source: "airportCode",
                inputType: FilterInputType.Text,
                label: "Airport Code",
              },
              {
                source: "customerPo",
                inputType: FilterInputType.Text,
                label: "Customer Po",
              },
              {
                source: "requestDate",
                inputType: FilterInputType.Date,
                label: "Request Date From",
                filterTypeChangeDisable: true,
                filterType: FilterTypes.GreaterThan,
              },
              {
                source: "requestDate",
                inputType: FilterInputType.Date,
                label: "Request Date To",
                filterTypeChangeDisable: true,
                filterType: FilterTypes.LessThan,
              },
              {
                source: "requestedDeliveryDate",
                inputType: FilterInputType.Date,
                label: "R.D.D. From",
                filterTypeChangeDisable: true,
                filterType: FilterTypes.GreaterThan,
              },
              {
                source: "requestedDeliveryDate",
                inputType: FilterInputType.Date,
                label: "R.D.D. To",
                filterTypeChangeDisable: true,
                filterType: FilterTypes.LessThan,
              },
              {
                source: "unsvcForwardDate",
                inputType: FilterInputType.Date,
                label: "Core Forward Date",
              },
              {
                source: "unsvcEstimatedDeliveryDate",
                inputType: FilterInputType.Date,
                label: "Core Estimated Delivery Date",
              },
              {
                source: "svcForwardDate",
                inputType: FilterInputType.Date,
                label: "Svc Forward Date",
              },
              {
                source: "svcEstimatedDeliveryDate",
                inputType: FilterInputType.Date,
                label: "Svc Estimated Delivery Date",
              },
              {
                source: "pbhRequestOrder.isLocalRepair",
                inputType: FilterInputType.Select,
                label: "Local Repair",
                filterTypeChangeDisable: true,
                filterType: FilterTypes.Equals,
                choices: LocalRepairChoices,
                optionValue: "id",
                optionText: "name",
                resettable: true,
              },
              {
                source: "isWaitingForCustomeRecord",
                inputType: FilterInputType.Select,
                label: "Is Waiting For Customer Record",
                filterTypeChangeDisable: true,
                filterType: FilterTypes.Equals,
                choices: IsWaitingForCustomeRecordChoices,
                optionValue: "id",
                optionText: "name",
                resettable: true,
              },
            ]}
          />
        }
      >
        <CopyableTableWrapper>
          <Datagrid optimized classes={{ table: classes.datagrid }}>
            <Actions label="Actions" />
            <TextField source="customerName" label="Customer" />
            <TextField source="refNo" label="Ref No" />
            <TextField source="category" label="Category" />
            <TextField source="pn" label="Pn" />
            <TextField source="airportCode" label="AirportCode" />
            <TextField source="acRegistration" label="Ac Reg" />
            <TextField source="customerPo" label="Customer Po" />
            <CustomDateField source="requestDate" label="Request Date" />
            <CustomDateField source="requestedDeliveryDate" label="Requested Delivery Date" />
            <TextField source="requestStatusText" label="Request Status" />
            <TextField source="svcPn" label="Svc PN" sortable={false} />
            <TextField source="svcSn" label="Svc SN" sortable={false} />
            <TextField source="svcAwb" label="Svc Awb" sortable={false} />
            <TextField source="unsvcPn" label="Core PN" sortable={false} />
            <TextField source="unsvcSn" label="Core SN" sortable={false} />
            <TextField source="unsvcAwb" label="Core Awb" sortable={false} />
            <TextField source="serviceable" label="Serviceable" sortable={false} />
            <TextField source="unserviceable" label="Core Unit" sortable={false} />
            {!user?.isCustomer && <TextField source="note" label="PBH NOTE" aria-multiline sortable={false} />}
          </Datagrid>
        </CopyableTableWrapper>
      </PoolList>
      <RequestRemark
        isMatchCreate={isMatchCreate}
        setIsMatchCreate={setIsMatchCreate}
        requestRemark={request}
        handleClose={refresh}
      />
    </>
  );
};

export default PbhSearch;
